import { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Plus,
  Trash2,
  Pencil,
  Image,
  X,
  Music,
  Video,
  List,
  AlignLeft,
  ClipboardPaste,
  Smile,
  Frown,
  ArrowLeftRight,
} from "lucide-react";
import { toast } from "sonner";
import type { TemplateField } from "@/types/activity";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MediaPickerDialog from "@/components/media/MediaPickerDialog";
import MediaPreviewButton from "@/components/media/MediaPreviewButton";

interface DynamicCardFieldsProps {
  fields: TemplateField[];
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  templateCode?: string;
  errorFieldNames?: Set<string>;
  disabled?: boolean;
}

/** Slugify a label string into a kebab-case ID */
function slugify(text: string): string {
  return (
    text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "option"
  );
}

/** Hook to fetch card_{templateCode}_default setting */
function useCardDefaults(templateCode?: string) {
  const [defaults, setDefaults] = useState<Record<string, Record<string, unknown>> | null>(null);

  useEffect(() => {
    if (!templateCode) return;
    const key = `card_${templateCode}_default`;
    supabase
      .from("settings")
      .select("value")
      .eq("key", key)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value && typeof data.value === "object" && !Array.isArray(data.value)) {
          setDefaults(data.value as Record<string, Record<string, unknown>>);
        }
      });
  }, [templateCode]);

  return defaults;
}

/** Helper: extract key from a select option */
function getOptionKey(opt: string | { key: string; label: string }): string {
  return typeof opt === "string" ? opt : opt.key;
}

/** Helper: extract label from a select option */
function getOptionLabel(opt: string | { key: string; label: string }): string {
  return typeof opt === "string" ? opt : opt.label;
}

/** Parse a visibility key into field names: "title-description-image_url" → ["title", "description", "image_url"] */
function parseVisibilityKey(key: string): string[] {
  return key.split("-").filter(Boolean);
}

export default function DynamicCardFields({
  fields,
  values,
  onChange,
  templateCode,
  errorFieldNames,
  disabled,
}: DynamicCardFieldsProps) {
  const cardDefaults = useCardDefaults(templateCode);
  const defaultsAppliedRef = useRef(false);

  // Apply template-level defaultValue on mount when values are empty
  // Also auto-initialize true_false options arrays
  useEffect(() => {
    if (defaultsAppliedRef.current) return;
    defaultsAppliedRef.current = true;
    const patch: Record<string, unknown> = {};
    for (const field of fields) {
      if (
        field.defaultValue !== undefined &&
        field.defaultValue !== null &&
        (values[field.name] === undefined || values[field.name] === null || values[field.name] === "")
      ) {
        patch[field.name] = field.defaultValue;
      }
      // Auto-initialize true_false options
      if (templateCode === "true_false" && field.type === "array" && field.item?.fields && (field.item.fields as TemplateField[]).length) {
        const current = values[field.name];
        if (!current || (Array.isArray(current) && (current as unknown[]).length === 0)) {
          const subFields = field.item.fields;
          const hasIdField = subFields.some((f) => f.name === "id");
          const hasLabelField = subFields.some((f) => f.name === "label");
          if (hasIdField) {
            const makeItem = (label: string, id: string) => {
              const item: Record<string, unknown> = {};
              subFields.forEach((f) => {
                if (f.type === "boolean") item[f.name] = false;
                else if (f.type === "integer") item[f.name] = 0;
                else if (f.type === "array") item[f.name] = [];
                else item[f.name] = "";
              });
              item.id = id;
              if (hasLabelField) item.label = label;
              return item;
            };
            patch[field.name] = [makeItem("Vrai", "true"), makeItem("Faux", "false")];
          }
        }
      }
    }
    if (Object.keys(patch).length > 0) {
      onChange({ ...values, ...patch });
    }
    return () => { defaultsAppliedRef.current = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = useCallback(
    (name: string, value: unknown) => {
      const selectField = fields.find((f) => f.name === name && f.type === "select");
      if (selectField && cardDefaults && typeof value === "string" && cardDefaults[value]) {
        const defaultValues = cardDefaults[value];
        onChange({ ...values, [name]: value, ...defaultValues });
      } else {
        onChange({ ...values, [name]: value });
      }
    },
    [fields, values, onChange, cardDefaults],
  );

  // Compute visible fields based on controlsVisibility
  const visibilitySelect = fields.find((f) => f.type === "select" && f.controlsVisibility);
  const selectedKey = visibilitySelect ? (values[visibilitySelect.name] as string) : undefined;
  const visibleFieldNames = visibilitySelect
    ? selectedKey
      ? new Set([...parseVisibilityKey(selectedKey), visibilitySelect.name])
      : new Set([visibilitySelect.name]) // no value yet → show only the select
    : null; // null = no controlsVisibility → show all

  // Build score_points linked map: arrayFieldName → { scoreFieldName, scoreValues }
  const linkedScoreFields = new Map<string, { fieldName: string; values: Record<string, number> }>();
  for (const f of fields) {
    if (f.type === "score_points" && f.linkedField) {
      linkedScoreFields.set(f.linkedField, {
        fieldName: f.name,
        values: (values[f.name] as Record<string, number>) || {},
      });
    }
  }

  const updateScorePoints = useCallback(
    (scoreFieldName: string, newScoreValues: Record<string, number>) => {
      onChange({ ...values, [scoreFieldName]: newScoreValues });
    },
    [values, onChange],
  );

  return (
    <fieldset disabled={disabled} className="space-y-4">
      {fields
        .filter((field) => field.type !== "score_points") // score_points rendered inline in array
        .filter((field) => !visibleFieldNames || visibleFieldNames.has(field.name))
        .map((field) => {
          const linked = linkedScoreFields.get(field.name);
          return (
            <FieldRenderer
              key={field.name}
              field={field}
              value={values[field.name]}
              onChange={(val) => update(field.name, val)}
              positionValue={field.hasPosition ? (values[`${field.name}_position`] as string) : undefined}
              onPositionChange={field.hasPosition ? (val) => update(`${field.name}_position`, val) : undefined}
              hasError={errorFieldNames?.has(field.name)}
              scorePoints={linked}
              onScorePointsChange={linked ? (sp) => updateScorePoints(linked.fieldName, sp) : undefined}
              onCombinedChange={
                linked
                  ? (arrayVal, spVal) => {
                      // Single atomic update: both array field and score_points
                      onChange({ ...values, [field.name]: arrayVal, [linked.fieldName]: spVal });
                    }
                  : undefined
              }
              templateCode={templateCode}
            />
          );
        })}
    </fieldset>
  );
}

function FieldRenderer({
  field,
  value,
  onChange,
  positionValue,
  onPositionChange,
  hasError,
  scorePoints,
  onScorePointsChange,
  onCombinedChange,
  templateCode,
}: {
  field: TemplateField;
  value: unknown;
  onChange: (val: unknown) => void;
  positionValue?: string;
  onPositionChange?: (val: string) => void;
  hasError?: boolean;
  scorePoints?: { fieldName: string; values: Record<string, number> };
  onScorePointsChange?: (sp: Record<string, number>) => void;
  /** Combined callback to update array + score_points atomically */
  onCombinedChange?: (arrayVal: unknown, scorePointsVal: Record<string, number>) => void;
  templateCode?: string;
}) {
  const label = field.label || field.name;
  const required = field.required;
  const errorClass = hasError ? "border-destructive ring-1 ring-destructive/30" : "";

  if (field.type === "boolean") {
    return (
      <div className={`flex items-center gap-3 ${hasError ? "rounded-md border border-destructive p-2" : ""}`}>
        <Switch checked={!!value} onCheckedChange={(checked) => onChange(checked)} />
        <Label>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      </div>
    );
  }

  if (field.type === "integer") {
    return (
      <div className="space-y-1.5">
        <Label>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
        <Input
          className={errorClass}
          type="number"
          value={value !== undefined && value !== null ? String(value) : ""}
          min={field.minimum}
          max={field.maximum}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === "" ? undefined : Number(v));
          }}
        />
      </div>
    );
  }

  if (field.type === "select" && field.options?.length) {
    return (
      <div className="space-y-1.5">
        <Label>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
        <Select value={(value as string) || ""} onValueChange={(v) => onChange(v)}>
          <SelectTrigger className={`h-9 text-sm ${errorClass}`}>
            <SelectValue placeholder="Sélectionner…" />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((opt) => (
              <SelectItem key={getOptionKey(opt)} value={getOptionKey(opt)}>
                {getOptionLabel(opt)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.type === "file") {
    return (
      <FileFieldRenderer
        field={field}
        value={(value as string) || ""}
        onChange={onChange}
        positionValue={positionValue}
        onPositionChange={onPositionChange}
        hasError={hasError}
      />
    );
  }

  if (field.type === "audio") {
    const audioVal = (value as { url?: string; duration_seconds?: number } | null) || {};
    return <AudioFieldRenderer field={field} value={audioVal} onChange={onChange} hasError={hasError} />;
  }

  if (field.type === "video") {
    return <VideoFieldRenderer field={field} value={(value as string) || ""} onChange={onChange} hasError={hasError} />;
  }

  if (field.type === "transcript") {
    return (
      <TranscriptFieldRenderer
        field={field}
        value={(value as string[]) || []}
        onChange={onChange}
        hasError={hasError}
      />
    );
  }

  if (field.type === "array") {
    return (
      <ArrayFieldRenderer
        field={field}
        value={(value as Record<string, unknown>[]) || []}
        onChange={onChange}
        hasError={hasError}
        scorePoints={scorePoints}
        onScorePointsChange={onScorePointsChange}
        onCombinedChange={onCombinedChange}
        templateCode={templateCode}
      />
    );
  }

  // string (default)
  const isTextarea = field.input === "textarea" || field.input === "richtext";

  return (
    <div className="space-y-1.5">
      <Label>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      {isTextarea ? (
        <Textarea
          className={errorClass}
          value={(value as string) || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
        />
      ) : (
        <Input
          className={errorClass}
          value={(value as string) || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.format === "uri" ? "https://..." : ""}
        />
      )}
    </div>
  );
}

/* ─── File field renderer ─────────────────────────────────── */

function FileFieldRenderer({
  field,
  value,
  onChange,
  positionValue,
  onPositionChange,
  hasError,
}: {
  field: TemplateField;
  value: string;
  onChange: (val: unknown) => void;
  positionValue?: string;
  onPositionChange?: (val: string) => void;
  hasError?: boolean;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const label = field.label || field.name;

  return (
    <div className={`space-y-1.5 ${hasError ? "rounded-md border border-destructive p-2" : ""}`}>
      <Label>
        {label}
        {field.required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex-1 justify-start text-xs truncate h-9"
          onClick={() => setPickerOpen(true)}
        >
          <Image className="h-3.5 w-3.5 mr-1 shrink-0" />
          {value ? value.split("/").pop() : "Choisir un média…"}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-destructive shrink-0"
            onClick={() => onChange("")}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
        {value && <MediaPreviewButton url={value} />}
      </div>
      {field.hasPosition && onPositionChange && (
        <div className="flex items-center gap-1 pt-1">
          <Label className="text-xs text-muted-foreground mr-1">Position :</Label>
          {[
            { value: "top", label: "Haut" },
            { value: "middle", label: "Milieu" },
            { value: "bottom", label: "Bas" },
            { value: "background_cover", label: "Fond d'écran" },
          ].map((opt) => (
            <Button
              key={opt.value}
              type="button"
              variant={positionValue === opt.value ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs px-2.5"
              onClick={() => onPositionChange(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      )}
      <MediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        currentUrl={value}
        onSelect={(url) => onChange(url || "")}
        bucket={field.bucket || "media"}
        mediaType="image"
      />
    </div>
  );
}

/* ─── Audio field renderer ────────────────────────────────── */

interface AudioValue {
  url?: string;
  duration_seconds?: number;
}

function AudioFieldRenderer({
  field,
  value,
  onChange,
  hasError,
}: {
  field: TemplateField;
  value: AudioValue;
  onChange: (val: unknown) => void;
  hasError?: boolean;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const label = field.label || field.name;
  const url = value.url || "";

  const handleSelect = (selectedUrl: string | undefined) => {
    const newUrl = selectedUrl || "";
    if (!newUrl) {
      onChange({ ...value, url: "", duration_seconds: undefined });
      return;
    }
    // Auto-detect duration
    const audio = new Audio(newUrl);
    audio.addEventListener("loadedmetadata", () => {
      onChange({ ...value, url: newUrl, duration_seconds: Math.round(audio.duration * 10) / 10 });
    });
    audio.addEventListener("error", () => {
      onChange({ ...value, url: newUrl });
    });
  };

  return (
    <div className={`space-y-1.5 ${hasError ? "rounded-md border border-destructive p-2" : ""}`}>
      <Label>
        {label}
        {field.required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex-1 justify-start text-xs truncate h-9"
          onClick={() => setPickerOpen(true)}
        >
          <Music className="h-3.5 w-3.5 mr-1 shrink-0" />
          {url ? url.split("/").pop() : "Choisir un audio…"}
        </Button>
        {url && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-destructive shrink-0"
            onClick={() => onChange({ url: "", duration_seconds: undefined })}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {url && <audio ref={audioRef} src={url} controls className="w-full h-8 mt-1" preload="metadata" />}
      {value.duration_seconds !== undefined && (
        <p className="text-xs text-muted-foreground">Durée : {value.duration_seconds}s</p>
      )}
      <MediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        currentUrl={url}
        onSelect={handleSelect}
        bucket={field.bucket || "audio"}
        mediaType="audio"
      />
    </div>
  );
}

/* ─── Video field renderer ─────────────────────────────────── */

function VideoFieldRenderer({
  field,
  value,
  onChange,
  hasError,
}: {
  field: TemplateField;
  value: string;
  onChange: (val: unknown) => void;
  hasError?: boolean;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const label = field.label || field.name;

  return (
    <div className={`space-y-1.5 ${hasError ? "rounded-md border border-destructive p-2" : ""}`}>
      <Label>
        {label}
        {field.required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex-1 justify-start text-xs truncate h-9"
          onClick={() => setPickerOpen(true)}
        >
          <Video className="h-3.5 w-3.5 mr-1 shrink-0" />
          {value ? value.split("/").pop() : "Choisir une vidéo…"}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-destructive shrink-0"
            onClick={() => onChange("")}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {value && <video src={value} controls className="w-full max-h-48 rounded mt-1" preload="metadata" />}
      <MediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        currentUrl={value}
        onSelect={(url) => onChange(url || "")}
        bucket={field.bucket || "video"}
        mediaType="video"
      />
    </div>
  );
}

/* ─── Transcript field renderer ───────────────────────────── */

function TranscriptFieldRenderer({
  field,
  value,
  onChange,
  hasError,
}: {
  field: TemplateField;
  value: string[];
  onChange: (val: unknown) => void;
  hasError?: boolean;
}) {
  const [mode, setMode] = useState<"text" | "list">("text");
  const lastInputRef = useRef<HTMLInputElement>(null);
  const segments = value || [];
  const label = field.label || field.name;

  const addSegment = () => {
    onChange([...segments, ""]);
    // Focus new segment after render
    setTimeout(() => lastInputRef.current?.focus(), 50);
  };

  const removeSegment = (index: number) => onChange(segments.filter((_, i) => i !== index));

  const updateSegment = (index: number, text: string) => {
    const updated = [...segments];
    updated[index] = text;
    onChange(updated);
  };

  const handleTextChange = (text: string) => {
    onChange(text.split("\n"));
  };

  const handleTextBlur = () => {
    onChange(segments.filter((l) => l.trim() !== ""));
  };

  return (
    <div className={`space-y-2 ${hasError ? "rounded-md border border-destructive p-2" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Label>
            {label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          {segments.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {segments.length} segment{segments.length > 1 ? "s" : ""}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant={mode === "text" ? "secondary" : "ghost"}
            size="icon"
            className="h-7 w-7"
            title="Mode texte"
            onClick={() => setMode("text")}
          >
            <AlignLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant={mode === "list" ? "secondary" : "ghost"}
            size="icon"
            className="h-7 w-7"
            title="Mode liste"
            onClick={() => setMode("list")}
          >
            <List className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {mode === "text" ? (
        <Textarea
          value={segments.join("\n")}
          onChange={(e) => handleTextChange(e.target.value)}
          onBlur={handleTextBlur}
          placeholder="Un segment par ligne…"
          rows={8}
          className="font-mono text-sm resize-none"
        />
      ) : (
        <>
          <ScrollArea className="h-[184px]">
            <div className="space-y-1 pr-3">
              {segments.map((seg, idx) => (
                <div key={idx} className="flex items-center gap-1">
                  <span className="text-xs text-muted-foreground w-6 text-right shrink-0">{idx + 1}.</span>
                  <Input
                    ref={idx === segments.length - 1 ? lastInputRef : undefined}
                    value={seg}
                    onChange={(e) => updateSegment(idx, e.target.value)}
                    placeholder={`Segment ${idx + 1}…`}
                    className="h-8 text-sm"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-destructive"
                    onClick={() => removeSegment(idx)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
          <Button type="button" variant="outline" size="sm" onClick={addSegment}>
            <Plus className="mr-1 h-3 w-3" />
            Ajouter un segment
          </Button>
        </>
      )}
    </div>
  );
}

/* ─── Editable slug ID component ──────────────────────────── */

function EditableSlugId({ index, id, onChange }: { index: number; id: string; onChange: (newId: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(id);

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="text-xs text-muted-foreground">#{index + 1} -</span>
        <Input
          className="h-5 text-xs px-1 py-0 w-36 inline"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(slugify(e.target.value) || e.target.value)}
          onBlur={() => {
            setEditing(false);
            if (draft && draft !== id) onChange(draft);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setEditing(false);
              if (draft && draft !== id) onChange(draft);
            }
            if (e.key === "Escape") {
              setEditing(false);
              setDraft(id);
            }
          }}
        />
      </span>
    );
  }

  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer group"
      title="Cliquer pour modifier l'ID"
      onClick={() => {
        setDraft(id);
        setEditing(true);
      }}
    >
      <span>
        #{index + 1} - {id}
      </span>
      <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

/* ─── Array file sub-field ────────────────────────────────── */

function ArrayFileField({
  field,
  value,
  onChange,
}: {
  field: TemplateField;
  value: string;
  onChange: (val: string) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const label = field.label || field.name;

  return (
    <div className="space-y-1">
      <Label className="text-sm">{label}</Label>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex-1 justify-start text-xs truncate h-8"
          onClick={() => setPickerOpen(true)}
        >
          <Image className="h-3.5 w-3.5 mr-1 shrink-0" />
          {value ? value.split("/").pop() : "Choisir un média…"}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive shrink-0"
            onClick={() => onChange("")}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
        {value && <MediaPreviewButton url={value} size="sm" />}
      </div>
      <MediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        currentUrl={value}
        onSelect={(url) => onChange(url || "")}
        bucket={field.bucket || "media"}
        mediaType="image"
      />
    </div>
  );
}

/* ─── Typology toggle (positive/negative smiley) ─────────── */

function TypologyToggle({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const isPositive = value !== "negative";
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={`h-8 w-8 shrink-0 relative ${isPositive ? "text-emerald-500 hover:text-emerald-600" : "text-red-500 hover:text-red-600"}`}
      onClick={() => onChange(isPositive ? "negative" : "positive")}
      title={isPositive ? "Positif — cliquer pour négatif" : "Négatif — cliquer pour positif"}
    >
      {isPositive ? <Smile className="h-4 w-4" /> : <Frown className="h-4 w-4" />}
      <ArrowLeftRight
        style={{ width: 10, height: 10 }}
        className={`absolute -top-0.5 -right-0.5 ${isPositive ? "text-emerald-500" : "text-red-500"}`}
      />
    </Button>
  );
}

/* ─── Array field renderer ────────────────────────────────── */

function ArrayFieldRenderer({
  field,
  value,
  onChange,
  hasError,
  scorePoints,
  onScorePointsChange,
  onCombinedChange,
  templateCode,
}: {
  field: TemplateField;
  value: unknown[];
  onChange: (val: unknown) => void;
  hasError?: boolean;
  scorePoints?: { fieldName: string; values: Record<string, number> };
  onScorePointsChange?: (sp: Record<string, number>) => void;
  /** Combined callback to update array + score_points atomically, avoiding stale closure issues */
  onCombinedChange?: (arrayVal: unknown, scorePointsVal: Record<string, number>) => void;
  templateCode?: string;
}) {
  const isTrueFalse = templateCode === "true_false";
  const items = value || [];
  const subFields = field.item?.fields || [];
  const isSimpleArray = subFields.length === 0; // e.g. item: { type: "string" }
  const hasIdField = subFields.some((f) => f.name === "id");
  const hasLabelField = subFields.some((f) => f.name === "label");
  const shouldAutoSlug = hasIdField && hasLabelField;

  // true_false initialization is now handled by DynamicCardFields useEffect

  const hasTypology = !!field.hasTypology;
  const hasCustomItems = !!field.hasCustomItems;

  // Normalize existing items: ensure typology & is_custom defaults
  useEffect(() => {
    if (!Array.isArray(items) || items.length === 0) return;
    let changed = false;
    const normalized = items.map((item: unknown) => {
      if (hasTypology && isSimpleArray && (typeof item === "string" || (typeof item === "object" && item !== null && !("typology" in (item as Record<string, unknown>))))) {
        changed = true;
        const val = typeof item === "string" ? item : (item as Record<string, unknown>).value ?? "";
        return { value: val, typology: "positive" };
      }
      if (typeof item === "object" && item !== null) {
        const obj = item as Record<string, unknown>;
        const patch: Record<string, unknown> = {};
        if (hasTypology && !("typology" in obj)) { patch.typology = "positive"; changed = true; }
        if (hasCustomItems && !("is_custom" in obj)) { patch.is_custom = false; changed = true; }
        if (Object.keys(patch).length > 0) return { ...obj, ...patch };
      }
      return item;
    });
    if (changed) {
      if (scorePoints && onCombinedChange) {
        onCombinedChange(normalized, scorePoints.values);
      } else {
        onChange(normalized);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Generate next custom-choice ID */
  const nextCustomId = () => {
    const existingCustomIds = items
      .map((it) => (it as Record<string, unknown>)?.id)
      .filter((id) => typeof id === "string" && (id as string).startsWith("custom-choice"));
    if (!existingCustomIds.includes("custom-choice")) return "custom-choice";
    let n = 2;
    while (existingCustomIds.includes(`custom-choice-${n}`)) n++;
    return `custom-choice-${n}`;
  };

  const addCustomItem = () => {
    const newItem: Record<string, unknown> = {};
    subFields.forEach((f) => {
      if (f.type === "boolean") newItem[f.name] = false;
      else if (f.type === "integer") newItem[f.name] = 0;
      else if (f.type === "array") newItem[f.name] = [];
      else newItem[f.name] = "";
    });
    newItem.id = nextCustomId();
    if (hasLabelField) newItem.label = "J'ai une autre idée";
    newItem.is_custom = true;
    if (hasTypology) newItem.typology = "positive";
    const newItems = [...items, newItem];
    if (scorePoints && onCombinedChange) {
      onCombinedChange(newItems, { ...scorePoints.values, [String(newItem.id)]: 0 });
    } else {
      onChange(newItems);
    }
  };

  const addItem = () => {
    if (isSimpleArray) {
      if (hasTypology) {
        onChange([...items, { value: "", typology: "positive" }]);
      } else {
        onChange([...items, ""]);
      }
      return;
    }
    const newItem: Record<string, unknown> = {};
    subFields.forEach((f) => {
      if (f.type === "boolean") newItem[f.name] = false;
      else if (f.type === "integer") newItem[f.name] = 0;
      else if (f.type === "array") newItem[f.name] = [];
      else newItem[f.name] = "";
    });
    if (hasTypology) newItem.typology = "positive";
    if (hasCustomItems) newItem.is_custom = false;
    if (shouldAutoSlug) {
      newItem.id = `option-${items.length + 1}`;
    } else if ("id" in newItem) {
      newItem.id = crypto.randomUUID().slice(0, 8);
    }
    const newItems = [...items, newItem];
    // Use combined callback to update array + score_points atomically
    if (scorePoints && newItem.id && onCombinedChange) {
      onCombinedChange(newItems, { ...scorePoints.values, [String(newItem.id)]: 0 });
    } else {
      onChange(newItems);
    }
  };

  const removeItem = (index: number) => {
    const removedItem = items[index] as Record<string, unknown> | undefined;
    const newItems = items.filter((_, i) => i !== index);
    // Use combined callback to update array + score_points atomically
    if (scorePoints && removedItem?.id && onCombinedChange) {
      const newSp = { ...scorePoints.values };
      delete newSp[String(removedItem.id)];
      onCombinedChange(newItems, newSp);
    } else {
      onChange(newItems);
    }
  };

  const updateSimpleItem = (index: number, val: string) => {
    const updated = [...items];
    updated[index] = val;
    onChange(updated);
  };

  const updateItem = (index: number, key: string, val: unknown) => {
    const updated = [...items];
    const oldItem = updated[index] as Record<string, unknown>;
    const oldId = String(oldItem.id || "");
    const item = { ...oldItem, [key]: val };
    if (shouldAutoSlug && key === "label" && typeof val === "string") {
      item.id = slugify(val);
    }
    updated[index] = item;
    // Sync score_points when ID changes via auto-slug
    if (scorePoints && key === "label" && shouldAutoSlug && onCombinedChange) {
      const newId = String(item.id);
      if (oldId !== newId) {
        const newSp = { ...scorePoints.values };
        const pts = newSp[oldId] ?? 0;
        delete newSp[oldId];
        newSp[newId] = pts;
        onCombinedChange(updated, newSp);
        return;
      }
    }
    onChange(updated);
  };

  const updateItemId = (index: number, newId: string) => {
    const updated = [...items];
    const oldId = String((updated[index] as Record<string, unknown>).id || "");
    updated[index] = { ...(updated[index] as Record<string, unknown>), id: newId };
    // Sync score_points: rename key
    if (scorePoints && oldId && onCombinedChange) {
      const newSp = { ...scorePoints.values };
      const pts = newSp[oldId] ?? 0;
      delete newSp[oldId];
      newSp[newId] = pts;
      onCombinedChange(updated, newSp);
    } else {
      onChange(updated);
    }
  };

  return (
    <div className={`space-y-3 ${hasError ? "rounded-md border border-destructive p-2" : ""}`}>
      <Label>
        {field.label || field.name}
        {field.required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <div className="space-y-2">
        {items.map((item, idx) => {
          if (isSimpleArray) {
            const simpleVal =
              hasTypology && typeof item === "object" && item !== null
                ? String((item as Record<string, unknown>).value || "")
                : String(item || "");
            const simpleTypology =
              hasTypology && typeof item === "object" && item !== null
                ? String((item as Record<string, unknown>).typology || "positive")
                : "positive";
            return (
              <div key={idx} className="flex items-center gap-2">
                {hasTypology && (
                  <TypologyToggle
                    value={simpleTypology}
                    onChange={(t) => {
                      const updated = [...items];
                      updated[idx] = { value: simpleVal, typology: t };
                      onChange(updated);
                    }}
                  />
                )}
                <Input
                  className="h-8 text-sm flex-1"
                  value={simpleVal}
                  onChange={(e) => {
                    if (hasTypology) {
                      const updated = [...items];
                      updated[idx] = { value: e.target.value, typology: simpleTypology };
                      onChange(updated);
                    } else {
                      updateSimpleItem(idx, e.target.value);
                    }
                  }}
                  placeholder={`Élément ${idx + 1}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  onClick={() => removeItem(idx)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            );
          }

          const obj = item as Record<string, unknown>;
          return (
            <div key={idx} className={`rounded-md border p-2 space-y-1.5 ${obj.is_custom ? "border-amber-400 bg-amber-50/50 dark:bg-amber-950/20" : "border-border bg-card"}`}>
              {shouldAutoSlug && !isTrueFalse && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    {hasTypology && (
                      <TypologyToggle
                        value={String(obj.typology || "positive")}
                        onChange={(t) => updateItem(idx, "typology", t)}
                      />
                    )}
                    <EditableSlugId
                      index={idx}
                      id={String(obj.id || "")}
                      onChange={(newId) => updateItemId(idx, newId)}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    {hasCustomItems && (
                      <Badge
                        variant={obj.is_custom ? "default" : "outline"}
                        className={`cursor-pointer text-xs ${obj.is_custom ? "bg-amber-500 hover:bg-amber-600 text-white border-amber-500" : "hover:bg-amber-50"}`}
                        onClick={() => {
                          const becomingCustom = !obj.is_custom;
                          const updated = [...items];
                          const item: Record<string, unknown> = { ...(updated[idx] as Record<string, unknown>), is_custom: becomingCustom };
                          if (becomingCustom) {
                            item.id = nextCustomId();
                          } else {
                            const label = String(item.label || "");
                            item.id = label ? slugify(label) : `option-${idx + 1}`;
                          }
                          const oldId = String(obj.id || "");
                          const newId = String(item.id);
                          updated[idx] = item;
                          if (scorePoints && oldId !== newId && onCombinedChange) {
                            const newSp = { ...scorePoints.values };
                            const pts = newSp[oldId] ?? 0;
                            delete newSp[oldId];
                            newSp[newId] = pts;
                            onCombinedChange(updated, newSp);
                          } else {
                            onChange(updated);
                          }
                        }}
                      >
                        Personnalisable
                      </Badge>
                    )}
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(idx)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}
              {isTrueFalse && shouldAutoSlug && (
                <span className="text-xs text-muted-foreground font-medium">
                  #{idx + 1} - {String(obj.id || "")}
                </span>
              )}
              {subFields.map((sf) => {
                if (shouldAutoSlug && sf.name === "id") return null;
                // Hide "points" sub-field when score_points is linked (rendered inline with "pts")
                if (sf.name === "points" && scorePoints) return null;

                if (sf.type === "boolean") {
                  return (
                    <div key={sf.name} className="flex items-center gap-2">
                      <Switch checked={!!obj[sf.name]} onCheckedChange={(c) => updateItem(idx, sf.name, c)} />
                      <Label className="text-sm">{sf.label || sf.name}</Label>
                    </div>
                  );
                }
                if (sf.type === "integer") {
                  return (
                    <div key={sf.name} className="flex items-center gap-2">
                      <Label className="text-sm shrink-0 w-24">{sf.label || sf.name}</Label>
                      <Input
                        type="number"
                        className="h-8 text-sm"
                        value={obj[sf.name] !== undefined ? String(obj[sf.name]) : ""}
                        onChange={(e) =>
                          updateItem(idx, sf.name, e.target.value === "" ? undefined : Number(e.target.value))
                        }
                      />
                    </div>
                  );
                }
                if (sf.type === "file") {
                  const fileVal = (obj[sf.name] as string) || "";
                  return (
                    <ArrayFileField
                      key={sf.name}
                      field={sf}
                      value={fileVal}
                      onChange={(val) => updateItem(idx, sf.name, val)}
                    />
                  );
                }
                if (sf.type === "array") {
                  if (sf.item?.fields?.length) {
                    return (
                      <div key={sf.name} className="pl-3 border-l-2 border-border">
                        <ArrayFieldRenderer
                          field={sf}
                          value={(obj[sf.name] as unknown[]) || []}
                          onChange={(val) => updateItem(idx, sf.name, val)}
                        />
                      </div>
                    );
                  }
                  const arr = (obj[sf.name] as string[]) || [];
                  return (
                    <div key={sf.name} className="flex items-center gap-2">
                      <Label className="text-sm shrink-0 w-24">{sf.label || sf.name}</Label>
                      <Input
                        className="h-8 text-sm"
                        value={arr.join(", ")}
                        placeholder="Valeurs séparées par des virgules"
                        onChange={(e) =>
                          updateItem(
                            idx,
                            sf.name,
                            e.target.value
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean),
                          )
                        }
                      />
                    </div>
                  );
                }
                return (
                  <div key={sf.name} className="flex items-center gap-2">
                    <Label className="text-sm shrink-0 w-24">{sf.label || sf.name}</Label>
                    <Input
                      className="h-8 text-sm"
                      value={(obj[sf.name] as string) || ""}
                      onChange={(e) => updateItem(idx, sf.name, e.target.value)}
                      placeholder={sf.format === "uri" ? "https://..." : ""}
                    />
                  </div>
                );
              })}
              {/* Inline score points */}
              {scorePoints && onScorePointsChange && obj.id && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm shrink-0 w-24 text-muted-foreground">Points</Label>
                  <Input
                    type="number"
                    className="h-8 text-sm w-20"
                    value={scorePoints.values[String(obj.id)] ?? 0}
                    onChange={(e) => {
                      const pts = e.target.value === "" ? 0 : Number(e.target.value);
                      onScorePointsChange({ ...scorePoints.values, [String(obj.id)]: pts });
                    }}
                  />
                  <span className="text-xs text-muted-foreground">pts</span>
                </div>
              )}
              {!shouldAutoSlug && !isTrueFalse && (
                <div className="flex items-center justify-between">
                  {hasTypology && (
                    <TypologyToggle
                      value={String(obj.typology || "positive")}
                      onChange={(t) => updateItem(idx, "typology", t)}
                    />
                  )}
                  <div className="flex-1" />
                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(idx)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {!isTrueFalse && items.length === 0 && templateCode === "likert" && scorePoints && onCombinedChange ? (
        <div className="flex gap-2">
          {[5, 7].map((count) => (
            <Button
              key={count}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const newItems: Record<string, unknown>[] = [];
                const newSp: Record<string, number> = {};
                for (let i = 1; i <= count; i++) {
                  const id = `option-${i}`;
                  const item: Record<string, unknown> = {};
                  subFields.forEach((f) => {
                    if (f.type === "boolean") item[f.name] = false;
                    else if (f.type === "integer") item[f.name] = 0;
                    else if (f.type === "array") item[f.name] = [];
                    else item[f.name] = "";
                  });
                  item.id = id;
                  if (hasLabelField) item.label = `Option ${i}`;
                  newItems.push(item);
                  newSp[id] = i;
                }
                onCombinedChange(newItems, newSp);
              }}
            >
              <Plus className="mr-1 h-3 w-3" />
              {count} options (1→{count} pts)
            </Button>
          ))}
        </div>
      ) : !isTrueFalse ? (
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={addItem}>
            <Plus className="mr-1 h-3 w-3" />
            Ajouter
          </Button>
          {hasCustomItems && (
            <Button type="button" variant="outline" size="sm" className="border-amber-400 text-amber-600 hover:bg-amber-50" onClick={addCustomItem}>
              <Plus className="mr-1 h-3 w-3" />
              Option personnalisable
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}
